// PanelRoomCard — the room card AS the command surface.
//
// The approved direction. There is no second screen: the card the board-runner
// already reads fluently grows to carry what a separate panel was invented for.
//
// THE ONE IDEA WORTH PROTECTING: every card shares ONE day window, so the now
// line lands at the same x-position all the way down the grid. That is the
// single genuine advantage a Gantt had — comparing rooms against a common clock
// — kept without a Gantt and without leaving the card grid. If a future change
// makes each card scale to its own case, that advantage is gone and the time
// bars become decoration.
//
// Alarm owns the TOP EDGE, so severity is readable from the grid without
// reading any card in full. One alarm at a time: emergency outranks uncovered
// outranks break-waiting. Two alarms competing for the same edge is how neither
// gets seen.

import React from "react";
import styles, { C, MONO } from "./panelCard.styles";
import { laneCoverage, laneAlarmState, shiftTag, breakDueState } from "../../domain/controlPanel";
import { compactStatusRow, BREAK_CHANNELS, breakTimeFor } from "../../domain/breaks";
import { to12h } from "../../domain/timeline";

const ALARM_LOOK = {
  emergency: { glyph: "!", word: "EMERGENCY", bg: C.alarmBg, fg: C.alarm, line: C.alarmLine },
  uncovered: { glyph: "∅", word: "UNCOVERED", bg: C.warnBg, fg: C.warn, line: C.warnLine },
  break: { glyph: "●", word: "BREAK WAITING", bg: C.navySoft, fg: C.navy, line: C.navyLine },
};

const FULL_WORD = {
  none: "not taken",
  due: "overdue",
  requested: "waiting",
  in_progress: "on break",
  done: "done",
};

const CHANNEL_LABEL = { breakfast: "Break", lunch: "Lunch", dinner: "Dinner", bathroom: "Bath" };

// Glyph AND word, never colour alone.
const BREAK_LOOK = {
  none: { text: "– none", bg: "#fbfcfe", fg: C.mutedSoft, line: C.line },
  due: { text: "! due", bg: C.warnBg, fg: C.warn, line: C.warnLine },
  requested: { text: "● waiting", bg: C.alarmBg, fg: C.alarm, line: C.alarmLine },
  in_progress: { text: "▸ on br.", bg: C.warnBg, fg: C.warn, line: C.warnLine },
  done: { text: "✓ done", bg: C.goodBg, fg: C.good, line: C.goodLine },
};

const initials = (name) =>
  String(name || "")
    .replace(/^dr\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const pct = (min, axisStart, span) =>
  span > 0 ? Math.max(0, Math.min(100, ((min - axisStart) / span) * 100)) : 0;

export default function PanelRoomCard({
  lane,
  room,
  axisStart,
  axisEnd,
  nowMin,
  alarms,
  breakRow,
  breakWindows,
  reliefRow,
  canAct,
  onAssign,
  onEditTimes,
  onConfirmRelief,
  onCoverBreak,
  onRelieve,
  onSendHome,
  onMessage,
  onStatus,
  onMoveRoom,
}) {
  const [moreOpen, setMoreOpen] = React.useState(false);
  const span = axisEnd - axisStart;
  const cases = Array.isArray(lane.cases) ? lane.cases : [];
  const last = cases.length ? cases[cases.length - 1] : null;
  const coverage = laneCoverage(lane);
  const alarm = laneAlarmState(lane.roomId, alarms);
  const look = alarm.top ? ALARM_LOOK[alarm.top] : null;

  const person = last?.label || "";
  const supervisor = last?.attending || "";
  const startMin = Number.isFinite(last?.startMin) ? last.startMin : null;
  const endMin = Number.isFinite(last?.endMin) ? last.endMin : null;

  const cells = compactStatusRow(breakRow ? breakRow.breaks : null).map((c) =>
    breakDueState(c.state, c.channel, breakWindows, nowMin) === "due"
      ? { ...c, state: "due" }
      : c
  );

  const remaining =
    endMin != null && Number.isFinite(nowMin) && endMin > nowMin
      ? `${Math.floor((endMin - nowMin) / 60)}h ${(endMin - nowMin) % 60}m left`
      : endMin != null && Number.isFinite(nowMin)
        ? "past scheduled end"
        : "";

  return (
    <article
      style={{
        ...styles.card,
        borderColor: look ? look.line : C.line,
      }}
      data-roomid={lane.roomId}
      data-alarm={alarm.top || ""}
    >
      {look ? (
        <div style={{ ...styles.alarmStrip, background: look.bg, color: look.fg }}>
          <span aria-hidden="true">{look.glyph}</span>
          <span>{look.word}</span>
        </div>
      ) : null}

      <div style={styles.body}>
        <div style={styles.head}>
          <span style={styles.room}>{lane.roomName}</span>
          {last?.onCall ? (
            <span style={styles.statusChip}>ON CALL</span>
          ) : room?.status && String(room.status).toLowerCase() !== "empty" ? (
            <span style={styles.statusChip}>{String(room.status).toUpperCase()}</span>
          ) : (
            <span style={styles.statusChip}>EMPTY</span>
          )}
          <span style={styles.sectionName}>{lane.sectionLabel}</span>
        </div>

        <div style={styles.staff}>
          <span style={styles.avatar} aria-hidden="true">{initials(person) || "—"}</span>
          <span style={styles.who}>
            <span style={styles.name}>{person || "No provider"}</span>
            <span style={styles.role}>{person ? "CRNA / AA" : "room unstaffed"}</span>
          </span>
          {canAct ? (
            <>
              <button
                type="button"
                style={styles.iconBtn}
                title="Swap staff"
                aria-label={`Swap staff in ${lane.roomName}`}
                onClick={() => onAssign(lane)}
              >
                ⇄
              </button>
              <button
                type="button"
                style={styles.iconBtn}
                title="Move to another room"
                aria-label={`Move ${person || "this provider"} to another room`}
                onClick={() => onMoveRoom(lane)}
              >
                →
              </button>
            </>
          ) : null}
          <span style={styles.coverBox}>
            <span style={styles.coverKey}>Cover to</span>
            <span
              style={{ ...styles.coverVal, ...(coverage.uncovered ? styles.coverShort : null) }}
            >
              {shiftTag(coverage.departMin, coverage.onCall)}
            </span>
            {/* Stated in minutes, never colour alone. */}
            {coverage.uncovered ? (
              <span style={{ display: "block", fontSize: 10.5, color: C.alarm, fontWeight: 700 }}>
                ∅ {coverage.shortfallMin} min short
              </span>
            ) : null}
          </span>
        </div>

        {startMin != null && endMin != null && span > 0 ? (
          <div>
            <div
              style={styles.track}
              role="img"
              aria-label={
                `Scheduled ${to12h(startMin)} to ${to12h(endMin)}.` +
                (Number.isFinite(nowMin) ? ` Now ${to12h(nowMin)}.` : "") +
                (coverage.uncovered
                  ? ` Cover ends ${to12h(coverage.departMin)}, ${coverage.shortfallMin} minutes before the case ends.`
                  : "")
              }
            >
              {[0.25, 0.5, 0.75].map((f) => (
                <span key={f} style={{ ...styles.tick, left: `${f * 100}%` }} />
              ))}

              <div
                style={{
                  ...styles.block,
                  left: `${pct(startMin, axisStart, span)}%`,
                  width: `${Math.max(1, pct(endMin, axisStart, span) - pct(startMin, axisStart, span))}%`,
                  borderStyle: last?.onCall ? "dashed" : "solid",
                }}
              >
                {Number.isFinite(nowMin) && nowMin > startMin ? (
                  <span
                    style={{
                      ...styles.blockDone,
                      width: `${Math.min(100, ((nowMin - startMin) / (endMin - startMin)) * 100)}%`,
                    }}
                  />
                ) : null}
              </div>

              {coverage.uncovered && Number.isFinite(coverage.departMin) ? (
                <span
                  style={{
                    ...styles.uncoveredStretch,
                    left: `${pct(coverage.departMin, axisStart, span)}%`,
                    width: `${Math.max(1, pct(endMin, axisStart, span) - pct(coverage.departMin, axisStart, span))}%`,
                  }}
                />
              ) : null}

              {Number.isFinite(coverage.departMin) && !coverage.onCall ? (
                <span style={{ ...styles.notch, left: `${pct(coverage.departMin, axisStart, span)}%` }} />
              ) : null}

              {Number.isFinite(nowMin) ? (
                <span style={{ ...styles.now, left: `${pct(nowMin, axisStart, span)}%` }} aria-hidden="true" />
              ) : null}
            </div>

            <div style={styles.legend}>
              <span>{to12h(startMin)}</span>
              <span>→</span>
              <span>{to12h(endMin)}</span>
              {remaining ? <span style={{ marginLeft: "auto" }}>{remaining}</span> : null}
            </div>
          </div>
        ) : null}

        <hr style={styles.rule} />

        <div style={styles.sup}>
          <span>Supervisor</span>
          <span style={styles.supChip}>MD</span>
          <span style={{ color: C.ink, fontWeight: 600 }}>{supervisor || "none"}</span>
          {last?.surgeon ? (
            <span style={{ marginLeft: "auto", color: C.mutedSoft }}>Surgeon: {last.surgeon}</span>
          ) : null}
        </div>

        <div style={styles.breaks}>
          {cells.map((c) => {
            const bl = BREAK_LOOK[c.state] || BREAK_LOOK.none;
            return (
              <span
                key={c.channel}
                style={{ ...styles.brk, background: bl.bg, borderColor: bl.line }}
                data-break={c.channel}
                title={`${CHANNEL_LABEL[c.channel] || c.channel}: ${FULL_WORD[c.state] || c.state}`}
              >
                <span style={styles.brkLab}>{CHANNEL_LABEL[c.channel] || c.channel}</span>
                <span style={{ ...styles.brkState, color: bl.fg }}>
                  {(() => {
                    // "✓ 11:55" when the room recorded when it happened;
                    // "✓ done" on older rooms that never did. Absent times
                    // simply render without one.
                    const at = breakTimeFor(room?.breakTimes, c.channel);
                    return at && (c.state === "done" || c.state === "in_progress")
                      ? `${bl.text.split(" ")[0]} ${at}`
                      : bl.text;
                  })()}
                </span>
              </span>
            );
          })}
        </div>

        {reliefRow ? (
          <div style={styles.relief}>
            <span aria-hidden="true">⇄</span>
            <span>
              {reliefRow.outgoingName || "—"} → {reliefRow.incomingName}
              {reliefRow.stale ? " · late" : " · staged"}
            </span>
            {canAct ? (
              <button
                type="button"
                style={{ ...styles.act, marginLeft: "auto", minHeight: 28 }}
                onClick={() => onConfirmRelief(reliefRow)}
              >
                Confirm
              </button>
            ) : null}
          </div>
        ) : null}

        {canAct ? (
          <div style={styles.acts}>
            {/* Frequent and cheaply reversible first; consequential behind More,
                each tagged with the confirm it will ask for, so the weight of an
                action is visible before it is opened. */}
            <button type="button" style={{ ...styles.act, ...styles.actPrimary }} onClick={() => onCoverBreak(lane)}>
              Break
            </button>
            <button type="button" style={styles.act} onClick={() => onRelieve(lane)}>
              Relieve
            </button>
            <button type="button" style={styles.act} onClick={() => onSendHome(lane)}>
              Home
            </button>
            <button type="button" style={styles.act} onClick={() => onMessage(lane)}>
              Message
            </button>
            <button type="button" style={styles.act} onClick={() => onEditTimes(lane)}>
              Times
            </button>
            <button type="button" style={styles.act} onClick={() => onStatus(lane, "closing")}>
              → Closing
            </button>

            <span style={styles.more}>
              <button
                type="button"
                style={{ ...styles.act, ...styles.actGhost }}
                aria-expanded={moreOpen}
                aria-haspopup="menu"
                onClick={() => setMoreOpen((v) => !v)}
              >
                More ▾
              </button>
              {/* ⚠️ NO role="menu" ON THE LIST BELOW, DELIBERATELY. role="menu"
                  promises menu keyboard semantics — arrow keys between items, focus
                  moved into the menu, Escape to leave — and none of that is
                  implemented. The children are real <button>s, already focusable and
                  operable by keyboard; the role added a contract nothing honoured and
                  made the container itself demand focus it never took. No role beats a
                  role you cannot keep. */}
              {moreOpen ? (
                <span style={styles.moreBody} onMouseLeave={() => setMoreOpen(false)}>
                  <span style={styles.moreCap}>Needs a second step</span>
                  <button type="button" style={styles.moreItem}
                    onClick={() => { setMoreOpen(false); onStatus(lane, "active"); }}>
                    Set active <span style={styles.moreTag}>Confirm</span>
                  </button>
                  <button type="button" style={styles.moreItem}
                    onClick={() => { setMoreOpen(false); onAssign(lane); }}>
                    Change supervisor <span style={styles.moreTag}>Confirm</span>
                  </button>
                  <button type="button" style={{ ...styles.moreItem, color: C.alarm }}
                    onClick={() => { setMoreOpen(false); onStatus(lane, "cancelled"); }}>
                    Cancel case <span style={styles.moreTag}>Confirm</span>
                  </button>
                </span>
              ) : null}
            </span>
          </div>
        ) : null}
      </div>
    </article>
  );
}

export { BREAK_CHANNELS };
